import type { Action } from '@reduxjs/toolkit';
import { useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import { AssetsListDetailTab } from '../containers/assets/details/types';
import {
    assetTableDisplayOptionsChanged,
    assetTableApplyFilterChanges,
    assetTableSearchChanged,
    assetTableSortChanges,
    changeAssetDetailsTab,
    getAssetDetailTab,
    getAssetTableSortColumn,
    getAssetTableSortDir,
    getSearchString,
    getShowActiveAssets,
    getShowAssetsWithDataSource,
    getShowAssetsWithoutDataSource,
    getShowArchivedAssets,
    getTableCompactRows,
    getTableGridLines,
    openEasyOnboardingDialog,
    setSelectedAssetId,
} from '../reducers/assets/assetsSlice';
import { createSortString, extractAssetId, makeRoute, parseRoute, parseSortString } from './routeHelper';
import { useRouteState, useUpdateRoute } from './routeHooks';

const RouteUpdater = () => {
    const dispatch = useAppDispatch();
    const { search, pathname } = useLocation();

    const selectedAssetId = useAppSelector(state => state.assets.selectedAssetId);
    const currentSearch = useAppSelector(getSearchString);
    const selectedSortBy = useAppSelector(getAssetTableSortColumn);
    const selectedSortDir = useAppSelector(getAssetTableSortDir);
    const showActiveAssets = useAppSelector(getShowActiveAssets);
    const showArchivedAssets = useAppSelector(getShowArchivedAssets);
    const selectedWithDataSource = useAppSelector(getShowAssetsWithDataSource);
    const selectedWithoutDataSource = useAppSelector(getShowAssetsWithoutDataSource);
    const selectedGridLines = useAppSelector(getTableGridLines);
    const selectedCompactRows = useAppSelector(getTableCompactRows);
    let selectedStatus = 'all';
    if (showActiveAssets && !showArchivedAssets) {
        selectedStatus = 'active';
    } else if (!showActiveAssets && showArchivedAssets) {
        selectedStatus = 'archived';
    } else if (!showActiveAssets && !showArchivedAssets) {
        selectedStatus = 'none';
    }
    const currentShowEasyOnboardingDialog = useAppSelector(state => state.assets.easyOnboardingDialog.show);

    const selectedTab = useAppSelector(getAssetDetailTab);
    const routeSearchParams = parseRoute(search);

    // Parse initial route or after it has changed by browser navigation or user input
    const hasHydratedRouteState = useRouteState(() => {
        const dispatchQue: Action[] = [];

        const assetId = extractAssetId(pathname);
        const { status, withDataSource, gridLines, tab, withoutDataSource, compactRows, sort, search, createAssetModal } =
            routeSearchParams;
        const routeSearch = search ?? '';
        const routeStatus = status ?? 'all';

        if (routeSearch !== currentSearch) {
            dispatchQue.push(assetTableSearchChanged(routeSearch));
        }

        const parsedSort = sort ? parseSortString(sort) : undefined;

        if (parsedSort && (parsedSort.sortBy !== selectedSortBy || parsedSort.sortDir !== selectedSortDir)) {
            dispatchQue.push(assetTableSortChanges(parsedSort));
        }

        if (tab && tab !== selectedTab) {
            if (tab === 'details') {
                dispatchQue.push(changeAssetDetailsTab(AssetsListDetailTab.ASSET_DETAILS));
            } else if (tab === 'group') {
                dispatchQue.push(changeAssetDetailsTab(AssetsListDetailTab.TAG_LIST));
            } else if (tab === 'device') {
                dispatchQue.push(changeAssetDetailsTab(AssetsListDetailTab.DEVICE_LIST));
            }
        }

        const routeShowWithDataSource =
            withDataSource !== undefined ? withDataSource === 'true' : withoutDataSource !== 'true';
        const routeShowWithoutDataSource = withoutDataSource !== undefined ? withoutDataSource === 'true' : true;
        const routeGridLines = gridLines === 'true';
        const routeCompactRows = compactRows === 'true';

        if (
            routeStatus !== selectedStatus ||
            routeShowWithDataSource !== selectedWithDataSource ||
            routeShowWithoutDataSource !== selectedWithoutDataSource
        ) {
            const showActiveAssets = routeStatus === 'all' || routeStatus === 'active';
            const showArchivedAssets = routeStatus === 'all' || routeStatus === 'archived';

            dispatchQue.push(
                assetTableApplyFilterChanges({
                    showAssetsWithDataSource: routeShowWithDataSource,
                    showAssetsWithoutDataSource: routeShowWithoutDataSource,
                    showActiveAssets,
                    showArchivedAssets,
                })
            );
        }

        if (routeGridLines !== selectedGridLines || routeCompactRows !== selectedCompactRows) {
            dispatchQue.push(
                assetTableDisplayOptionsChanged({
                    gridLines: routeGridLines,
                    compactRows: routeCompactRows,
                })
            );
        }

        if (createAssetModal === 'show' && !currentShowEasyOnboardingDialog) {
            dispatchQue.push(openEasyOnboardingDialog());
        }

        if (assetId !== selectedAssetId) {
            dispatchQue.push(setSelectedAssetId(assetId ?? null));
        }

        for (const action of dispatchQue) {
            dispatch(action);
        }
    });

    // Update route whenever an observed store prop changes.
    // The key of the RouteState is the search param key in the URL.
    const newRoute = makeRoute({
        status: selectedStatus === 'all' ? undefined : selectedStatus,
        tab: selectedTab === AssetsListDetailTab.ASSET_DETAILS ? undefined : selectedTab,
        withDataSource: selectedWithDataSource ? undefined : 'false',
        gridLines: selectedGridLines ? 'true' : undefined,
        withoutDataSource: selectedWithoutDataSource ? undefined : 'false',
        compactRows: selectedCompactRows ? 'true' : undefined,
        sort: createSortString({ sortBy: selectedSortBy, sortDir: selectedSortDir }),
        search: encodeURIComponent(currentSearch),
        createAssetModal: currentShowEasyOnboardingDialog ? 'show' : undefined,
        step: currentShowEasyOnboardingDialog ? routeSearchParams.step : undefined,
        vehicle_type: currentShowEasyOnboardingDialog ? routeSearchParams.vehicle_type : undefined,
        brand: currentShowEasyOnboardingDialog ? routeSearchParams.brand : undefined,
        device_type: currentShowEasyOnboardingDialog ? routeSearchParams.device_type : undefined,
    });

    useUpdateRoute({
        newSubResource: selectedAssetId ?? undefined,
        newSearch: newRoute,
        enabled: hasHydratedRouteState,
    });

    return null;
};

export default RouteUpdater;
